<template>
  <draggable
    :list="value"
    :group="{ name: 'shoppable', pull: true, put: ['shoppable'] }"
    class="tgbDropArea tgbDropArea--shoppable"
    :class="{ 'empty': !value.length }"
    data-test="producer-shoppable-droparea"
    @change="addToShoppable"
  >
    <p v-if="!value.length && !addingShoppableItem" class="tgbDropArea--shoppable__help">Drag shoppable here</p>

    <div
      v-else
      v-for="p in value"
      :key="p.stuffId"
      class="tgbDropArea--shoppable__item"
    >
      <div class="tgbDropArea--shoppable__media">
        <div v-if="addingShoppableItem" class="flex items-center content-center h-full">
          <span
            class="fas fa-spinner fa-spin"
          ></span>
        </div>
        <img
          v-else
          class="tgbDropArea--shoppable__image"
          :src="p.image"
          :alt="p.name"
        >
      </div>

      <div
        v-if="value.length"
        class="h-full flex flex-column content-between items-center margin-left-xxs"
      >
        <span class="shoppable-dropzone-store-icon" :class="storeIcon"></span>

        <button
          class="tb-btn tb-btn-link xs text-danger text-xs"
          @click="$emit('remove', p)"
        >
          <span class="far fa-trash-alt"></span>
        </button>
      </div>
    </div>
  </draggable>
</template>

<script>
import { mapState } from 'vuex';
import Draggable from 'vuedraggable';
import catchify from 'catchify';
import Shortener from '@/js/services/shortener';
import { getStoreIcon } from '@/js/utils/shoppable';
import { encodeAffiliateMeta } from '../../../utils/affiliate';
import { getEnvironment } from '../../../utils/config';

/**
 * @typedef {Object} FanaticsProduct
 * @property {string} Id
 * @property {string} CatalogId
 * @property {string} CampaignId
 * @property {string} CampaignName
 * @property {string} CatalogItemId
 * @property {string} Name
 * @property {string} Description
 * @property {string} Manufacturer
 * @property {string} Url
 * @property {string} ImageUrl
 * @property {string} Currency
 * @property {string} CurrentPrice
 */

/**
 * @typedef {FanaticsProduct} AffiliateProduct
 */

export default {
  name: 'ShoppableDropzone',
  components: {
    Draggable,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
      required: true,
    },
    graphic: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    element: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },
  data() {
    return {
      addingShoppableItem: false,
    };
  },
  computed: {
    ...mapState({
      production: (store) => store.production.production,
    }),
    ...mapState('shoppable', [
      'amazonTrackingId',
    ]),

    storeIcon() {
      const { type } = this.value[0];

      return getStoreIcon(type);
    },
  },
  methods: {
    async addToShoppable({ added, removed }) {
      if (removed) {
        this.$emit('remove', removed);
        return;
      }

      // set shoppable element fields
      const selectedProduct = added.element?.rawData || added.element;

      this.addingShoppableItem = true;
      const affiliateUrl = this.makeAffiliate(selectedProduct.sourceUrl, selectedProduct.source);
      selectedProduct.shortUrl = await this.shortenUrl(affiliateUrl);
      selectedProduct.stuffId = added.element.stuffId;
      selectedProduct.affiliateUrl = affiliateUrl;

      this.$emit('input', selectedProduct);

      this.addingShoppableItem = false;
    },
    makeAffiliate(url, affiliate) {
      switch (affiliate) {
        case 'fanatics':
          return this.getFanaticsLink(url);
        case 'amazon':
          if (this.amazonTrackingId) {
            const newUrl = new URL(url);
            newUrl.searchParams.set('tag', this.amazonTrackingId);
            return newUrl.href;
          }

          return url;
        default:
          return url;
      }
    },

    /**
     * @param {string} url
     * @returns {string}
     */
    getFanaticsLink(url) {
      const newUrl = new URL(url);
      const selectedProduct = this.value[0].sourceItem;
      // NOTE: The encoded string of this object can't
      // exceed 255 characters for Fanatics/Impact, so we need to be careful
      // of what values we add here.
      const affiliateLinkData = {
        affiliate: 'fanatics',
        ownerId: this.production.owner,
        productionId: this.production.id,
        graphicTemplateId: this.graphic.template_id,
        catalogId: selectedProduct?.CatalogId,
        campaignId: selectedProduct?.CampaignId,
        sku: selectedProduct?.CatalogItemId,
        timestamp: new Date().getTime(),
        env: getEnvironment(),
      };
      const subId3 = encodeAffiliateMeta(affiliateLinkData);

      newUrl.searchParams.append('subId1', this.production.owner);
      newUrl.searchParams.append('subId2', this.production.id);
      newUrl.searchParams.append('subId3', subId3);

      return newUrl.href;
    },

    async shortenUrl(url) {
      const productionInfo = {
        user: this.production.owner,
        production: {
          id: this.production.id,
          title: this.production.title,
        },
        item: this.graphic,
        element: this.element,
      };

      const [err, resp] = await catchify(
        Shortener.getShortURL(url, productionInfo),
      );

      if (err) {
        this.errorToast(err?.message || 'Failed to shorten URL');
        return false;
      }

      const { shortUrl } = resp.data;
      return shortUrl;
    },
  },
};
</script>

<style lang="scss">
.shoppable-dropzone-store-icon {
  font-size: 14px;
  padding-top: 4px;
}
</style>
